import { useState, useEffect } from "react";
import { Menu, X, User, Home, CreditCard, PlayCircle, Store, Info, LogIn, UserPlus, Briefcase, Phone } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { getToken } from "../utils/tokenManager";
import { motion, AnimatePresence } from "framer-motion";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const { authState } = useAuth();
  const { isAuthenticated } = authState;
  const [isScrolled, setIsScrolled] = useState(false);
  const [hasActiveCard, setHasActiveCard] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/") {
      setIsScrolled(false);
    } else {
      setIsScrolled(true);
    }

    let scrollTimeout = null;
    const handleScroll = () => {
      if (scrollTimeout) return;
      scrollTimeout = setTimeout(() => {
        if (location.pathname === "/") {
          if (window.scrollY > 20) {
            setIsScrolled(true);
          } else {
            setIsScrolled(false);
          }
        }
        scrollTimeout = null;
      }, 100);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [location.pathname]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        !event.target.closest(".mobile-menu") &&
        !event.target.closest(".mobile-menu-button")
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const checkLoyaltyCard = async () => {
      if (isAuthenticated && authState.authType === "user") {
        try {
          const token = getToken();
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/user/card`,
            {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          if (response.ok) {
            const data = await response.json();
            if (data.card_number) {
              setHasActiveCard(true);
            } else {
              setHasActiveCard(false);
            }
          } else {
            setHasActiveCard(false);
          }
        } catch (error) {
          console.error("Error fetching loyalty card status:", error);
          setHasActiveCard(false);
        }
      } else {
        setHasActiveCard(false);
      }
    };

    checkLoyaltyCard();
  }, [isAuthenticated, authState.authType, location.pathname]);

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const getNavLinkClass = (path) => {
    const active = isActive(path);
    return `relative rounded-full font-semibold transition-all duration-300 ease-in-out focus:outline-none overflow-hidden whitespace-nowrap ${
      isScrolled
        ? "px-2.5 py-1 xl:px-3.5 xl:py-1.5 2xl:px-4 2xl:py-2"
        : "px-3 py-1.5 xl:px-4 xl:py-2 2xl:px-5 2xl:py-2.5"
    } ${
      active
        ? "bg-gradient-to-r from-[#CF344A] to-[#971132] text-white shadow-lg shadow-[#CF344A]/30 font-bold scale-[1.02] border border-white/70 ring-2 ring-[#CF344A]/25"
        : "border border-transparent hover:bg-gradient-to-r hover:from-[#CF344A] hover:to-[#971132] hover:text-white hover:shadow-md hover:shadow-[#CF344A]/25 hover:scale-105"
    }`;
  };

  const menuVariants = {
    open: {
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.05,
        delayChildren: 0.1
      }
    },
    closed: {
      x: "-100%",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.05,
        staggerDirection: -1
      }
    }
  };

  const itemVariants = {
    open: {
      opacity: 1,
      x: 0,
      transition: { type: "spring", stiffness: 300, damping: 24 }
    },
    closed: {
      opacity: 0,
      x: -20,
      transition: { type: "spring", stiffness: 300, damping: 24 }
    }
  };

  return (
    <header className="fixed top-0 left-0 w-full z-50 pointer-events-auto xl:pointer-events-none">
      <div
        className={`pointer-events-auto mx-auto transition-all duration-500 ease-in-out ${
          isScrolled || location.pathname !== "/"
            ? "mt-1.5 w-[96%] xl:w-[95%] 2xl:w-[90%] max-w-7xl rounded-2xl xl:rounded-full bg-[#47001C]/85 border border-rose-500/20 shadow-2xl xl:backdrop-blur-md p-2 xl:px-4 xl:py-2 2xl:p-3"
            : "mt-1.5 w-[96%] xl:w-[96%] 2xl:w-[92%] max-w-[92rem] 2xl:max-w-[94rem] rounded-2xl xl:rounded-full bg-transparent border-transparent shadow-none p-3 xl:px-4 xl:py-3 2xl:p-4 transition-all duration-300"
        }`}
      >
        <div className="w-full flex justify-between items-center px-1 xl:px-2 2xl:px-4">
          {/* Mobile Menu Button - Left side */}
          <div className="flex xl:hidden items-center">
            <button
              className={`mobile-menu-button transition-all duration-300 ease-in-out ${
                isOpen
                  ? "text-white rotate-90"
                  : isScrolled
                  ? "text-white"
                  : "text-[#47001C]"
              } drop-shadow-md hover:scale-110 p-2.5`}
              onClick={handleMenuToggle}
              aria-label={isOpen ? "Close menu" : "Open menu"}
              aria-expanded={isOpen}
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
            {isAuthenticated && (
              <Link
                to="/profile/dashboard"
                className={`group flex items-center justify-center p-2 rounded-full transition-colors duration-200 ml-4 ${
                  isScrolled ? "hover:bg-white/10" : "hover:bg-[#47001C]/10"
                }`}
                aria-label="Go to profile"
              >
                <User
                  size={22}
                  className={`transition-colors duration-300 ${
                    isScrolled ? "text-white" : "text-[#47001C]"
                  }`}
                />
              </Link>
            )}
          </div>

          {/* Logo - Right in mobile, left in desktop */}
          <Link
            to="/"
            className="text-2xl font-bold text-white drop-shadow-md order-last xl:order-first flex-shrink-0"
          >
            <img
              src={isScrolled ? "/images/Love_All.avif" : "/images/Love_All_Logo_Black_text.png"}
              className={`transition-all duration-500 ease-in-out ${
                isScrolled
                  ? "w-16 h-8 xl:w-20 xl:h-8"
                  : "w-20 h-10 xl:w-32 xl:h-14 2xl:w-36 2xl:h-16"
              } object-contain`}
              alt="Love All Logo"
            />
          </Link>

          {/* Desktop Nav */}
          <nav
            className={`hidden xl:flex items-center space-x-1 xl:space-x-1.5 2xl:space-x-2 ${
              isScrolled ? "text-[#FBD784]" : "text-[#47001C]"
            } drop-shadow-md font-semibold transition-all duration-500 ease-in-out text-sm xl:text-base whitespace-nowrap`}
          >
            <Link to="/" className={getNavLinkClass("/")}>
              <span className="relative z-10">Home</span>
            </Link>
            {!hasActiveCard ? (
              <Link to="/donate-and-join" className={getNavLinkClass("/donate-and-join")}>
                <span className="relative z-10">Donate & Join</span>
              </Link>
            ) : (
              <Link to="/my-qr" className={getNavLinkClass("/my-qr")}>
                <span className="relative z-10">My Card</span>
              </Link>
            )}
            <Link to="/our-work" className={getNavLinkClass("/our-work")}>
              <span className="relative z-10">Our Work</span>
            </Link>
            <Link to="/partners-and-rewards" className={getNavLinkClass("/partners-and-rewards")}>
              <span className="relative z-10">Partners & Rewards</span>
            </Link>
            <Link to="/loveall-media" className={getNavLinkClass("/loveall-media")}>
              <span className="relative z-10">LoveAll Media</span>
            </Link>
            <Link to="/about" className={getNavLinkClass("/about")}>
              <span className="relative z-10">About Us</span>
            </Link>
            <Link to="/contact" className={getNavLinkClass("/contact")}>
              <span className="relative z-10">Contact Us</span>
            </Link>
          </nav>

          {/* Desktop CTA Buttons */}
          <div
            className={`hidden xl:flex items-center flex-shrink-0 transition-all duration-500 ease-in-out ${
              isScrolled ? "space-x-2 2xl:space-x-2.5" : "space-x-2.5 2xl:space-x-3"
            }`}
          >
            {isAuthenticated ? (
              <Link
                to="/profile/dashboard"
                className={`group relative flex items-center space-x-1.5 rounded-full border-2 font-medium transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-lg focus:outline-none overflow-hidden whitespace-nowrap before:absolute before:inset-0 before:scale-0 hover:before:scale-100 before:transition-transform before:duration-300 before:ease-out before:-z-10 ${
                  isScrolled
                    ? "border-white/80 text-white hover:border-white hover:text-[#47001C] focus:ring-white/50 before:bg-white px-3.5 py-1.5 text-sm xl:text-base"
                    : "border-[#47001C]/80 text-[#47001C] hover:border-[#47001C] hover:text-white focus:ring-[#47001C]/50 before:bg-[#47001C] px-5 py-2 text-sm xl:text-base"
                }`}
              >
                <User
                  size={isScrolled ? 16 : 18}
                  className="relative z-10 transition-transform duration-300 group-hover:rotate-12 flex-shrink-0"
                />
                <span className="relative z-10">Profile</span>
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`relative rounded-full border-2 font-medium transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-lg focus:outline-none overflow-hidden whitespace-nowrap before:absolute before:inset-0 before:scale-0 hover:before:scale-100 before:transition-transform before:duration-300 before:ease-out before:-z-10 ${
                    isScrolled
                      ? "border-white/80 text-white hover:border-white hover:text-[#47001C] focus:ring-white/50 before:bg-white px-4 py-1.5 text-sm xl:text-base"
                      : "border-[#47001C]/80 text-[#47001C] hover:border-[#47001C] hover:text-white focus:ring-[#47001C]/50 before:bg-[#47001C] px-5 py-2 text-sm xl:text-base"
                  }`}
                >
                  <span className="relative z-10">Login</span>
                </Link>
                <Link
                  to="/register"
                  className={`relative rounded-full bg-gradient-to-r from-[#CF344A] to-[#971132] text-white font-semibold transition-all duration-300 ease-in-out hover:from-[#b02235] hover:to-[#7d0d28] hover:scale-105 hover:shadow-lg hover:shadow-[#CF344A]/25 focus:outline-none active:scale-95 whitespace-nowrap ${
                    isScrolled
                      ? "px-5 py-1.5 text-sm xl:text-base"
                      : "px-5 py-2 text-sm xl:text-base"
                  }`}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <div className="xl:hidden fixed inset-0 z-40 pointer-events-auto">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={closeMenu}
            ></motion.div>

            {/* Sliding Menu */}
            <motion.nav
              initial="closed"
              animate="open"
              exit="closed"
              variants={menuVariants}
              className="mobile-menu fixed top-0 left-0 h-full w-3/4 bg-gradient-to-b from-[#971132] via-[#7a0d28] to-[#47001C] shadow-2xl overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col min-h-full">
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b border-white/20">
                  <img
                    src="/images/Love_All.avif"
                    alt="Love All Logo"
                    className="h-8 object-contain"
                  />
                  <button
                    onClick={closeMenu}
                    className="text-white hover:text-rose-300 transition-colors duration-200 p-1"
                    aria-label="Close menu"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Mobile Login and Signup - Only show when NOT authenticated */}
                {!isAuthenticated && (
                  <motion.div variants={itemVariants} className="pt-6 px-5 space-y-3">
                    <div className="flex flex-col space-y-3">
                      <Link
                        to="/login"
                        onClick={closeMenu}
                        className="flex items-center justify-center gap-2 bg-white/10 border border-white/30 text-white px-4 py-4 rounded-full hover:bg-white hover:text-red-600 transition-all duration-300 font-medium backdrop-blur-sm w-full"
                      >
                        <LogIn className="w-[18px] h-[18px] min-w-[18px] flex-shrink-0" />
                        <span>Login</span>
                      </Link>
                      <Link
                        to="/register"
                        onClick={closeMenu}
                        className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#CF344A] to-[#971132] text-white px-4 py-4 rounded-full hover:from-[#b02235] hover:to-[#7d0d28] transition-all duration-300 font-medium shadow-lg w-full"
                      >
                        <UserPlus className="w-[18px] h-[18px] min-w-[18px] flex-shrink-0" />
                        <span>Sign Up</span>
                      </Link>
                    </div>
                  </motion.div>
                )}

                {/* Navigation Links */}
                <div className="flex-1 flex flex-col justify-start mt-6 space-y-2 px-6 mb-8">
                  {[
                    { to: "/", label: "Home", icon: Home },
                    ...(hasActiveCard ? [{ to: "/my-qr", label: "My Card", icon: CreditCard }] : [{ to: "/donate-and-join", label: "Donate & Join", icon: CreditCard }]),
                    { to: "/our-work", label: "Our Work", icon: Briefcase },
                    { to: "/partners-and-rewards", label: "Partners & Rewards", icon: Store },
                    { to: "/loveall-media", label: "LoveAll Media", icon: PlayCircle },
                    { to: "/about", label: "About Us", icon: Info },
                    { to: "/contact", label: "Contact Us", icon: Phone }
                  ].map((item) => (
                    <motion.div key={item.to} variants={itemVariants}>
                      <Link
                        to={item.to}
                        onClick={closeMenu}
                        className={`flex items-center gap-3 transition-all duration-300 font-medium text-left py-3 px-4 rounded-xl ${
                          isActive(item.to) 
                            ? "bg-white/20 text-white shadow-md border border-white/20" 
                            : "text-white/80 hover:bg-white/10 hover:text-white border border-transparent"
                        }`}
                      >
                        <item.icon className={`${isActive(item.to) ? 'text-white' : 'text-rose-200'} w-[20px] h-[20px] min-w-[20px] flex-shrink-0`} />
                        <span className="text-sm">{item.label}</span>
                      </Link> 
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.nav>
          </div>
        )}
      </AnimatePresence>
    </header>
  );
}
